import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { AtlCarouselContainer, AtlCarouselProps } from './carousel.props.ts';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../global-styles';
import { ButtonEnums } from '../button/index.ts';
import { enumAttributeConverter } from '../enum-types.ts';
import { ifDefined } from 'lit/directives/if-defined.js';

import * as TagNames from './carousel.tagnames.ts';

@customElement(TagNames.Carousel)
export class AtlCarousel extends LitElement implements AtlCarouselProps {
    
    declare slideContainers: AtlCarouselContainer[];
    declare containerClassName?: string;
    declare variant?: ButtonEnums.ButtonVariant;
    declare size?: ButtonEnums.ButtonSize;
    protected declare slideIndex: number;
    protected declare hideNavItems: boolean;
    protected declare nextDisabled: boolean;
    protected declare previousDisabled: boolean;
    protected declare mouseDown: boolean;

    static properties = {
        slideContainers: { type: Object },
        containerClassName: { type: String },
        variant: {type: String, converter: enumAttributeConverter(ButtonEnums.ButtonVariant, ButtonEnums.ButtonVariant.Cta) },
        size: {type: String, converter: enumAttributeConverter(ButtonEnums.ButtonSize, ButtonEnums.ButtonSize.Medium) },
        slideIndex: { type: Number, state: true },
        hideNavItems: { type: Boolean, state: true },
        nextDisabled: { type: Boolean, state: true },
        previousDisabled: { type: Boolean, state: true },
        mouseDown: { type: Boolean, state: true }
    };

    constructor(){
        super();
       
        this.variant = undefined;
        this.size = undefined;
        this.slideIndex = 0;
        this.hideNavItems = false;
        this.nextDisabled = false;
        this.previousDisabled = false;
        this.mouseDown = false;
    }


    static styles = [
        ...getGlobalStyleSheets(),
        css`    
            .atl-carousel-container {
                display: flex;
                overflow-x: auto;
                overflow-y: hidden;
                position: absolute;
                width: 100%;
                column-gap: 1rem;
            }
            .atl-carousel-container > div {
                width: fit-content;
                margin: 2px 
            }
            .atl-carousel-container::-webkit-scrollbar {
                display: none;
            }
            #atl-wc-carousel-btns-id{
                display: none;
            }
        `
    ];
    
    async connectedCallback() {
        super.connectedCallback();
        
        await this.updateComplete;
        this.setNavItemsVisibility(500)

        window.addEventListener('resize', async () => {
            this.setNavItemsVisibility(500)
        });
        window.addEventListener('mouseup', () => {
            this.mouseDown = false;
        })
    }
    
    disconnectedCallback() {
        super.connectedCallback();

        window.removeEventListener('resize', () => {});
        window.removeEventListener('mouseup', () => {})
    }

    renderSlides(){
        return this.slideContainers?.map((sc, i) => {
            return (
                html`
                    <div id=${`atl-wc-slideIndex${i}`}>
                        <slot name=${`${sc.id}`}></slot>
                    </div>
                `
            )
        })
    }

    renderNavItems(){
        if (this.hideNavItems) return nothing;

        return (
            html`
                <div id="atl-wc-carousel-btns-id" style="bottom: 0; position: absolute; right: 0;">
                    <atlantis-button 
                        variant=${this.variant ?? ButtonEnums.ButtonVariant.Action}
                        type=${ButtonEnums.ButtonType.Button}
                        size=${this.variant ?? ButtonEnums.ButtonSize.Large}
                        .disabled=${this.previousDisabled}
                        iconName='chevron-left'
                        @mousedown=${() => this.onMouseDown(false)}
                        @click=${() => this.lastSlide(this.slideIndex - 1)}
                    >
                    </atlantis-button>
                    <atlantis-button 
                        variant=${this.variant ?? ButtonEnums.ButtonVariant.Action}
                        type=${ButtonEnums.ButtonType.Button}
                        size=${this.variant ?? ButtonEnums.ButtonSize.Large}
                        .disabled=${this.nextDisabled}
                        iconName='chevron-right'
                        @mousedown=${() => this.onMouseDown(true)}
                        @click=${() => this.nextSlide(this.slideIndex + 1)}
                    >
                    </atlantis-button>
                </div>
            `
        )
    }

    render() {  
          
        return html`
            <div id="atl-wc-carousel-root-container-id" class=${ifDefined(this.containerClassName)} style="position: relative">
                <div>
                    <div id="atl-wc-carousel-container-id" class=${classNames("atl-carousel-container")}>
                        ${this.renderSlides()}
                    </div>
                </div>
                ${this.renderNavItems()}
            </div>
        `;
    }    

    private onMouseDown(next: boolean) {
        this.mouseDown = true;

        this.handleMouseDown(next, this.slideIndex)
    }     
    
    private handleMouseDown(next: boolean, newIndex: number) {
        newIndex = next ? newIndex + 1 : newIndex - 1;
        setTimeout(() => {
            if (this.mouseDown && (newIndex >= 0 || newIndex < this.slideContainers?.length)){
                if (next){
                    this.nextSlide(newIndex)                    
                }
                else{
                    this.lastSlide(newIndex)
                }
                this.handleMouseDown(next, newIndex)
            }
        }, 500);
    } 

    private nextSlide(newIndex: number) {
        if (newIndex > (this.slideContainers?.length - 1) || this.nextDisabled) {
            return;
        };
        const firstSlide = this.getSlide(0);
        const existingScrolledSlide = this.getSlide(newIndex - 1);
        const slideToScrollTo = this.getSlide(newIndex);
        const container = this.getContainer();
        const newLeft = Math.abs(firstSlide.getBoundingClientRect().left - slideToScrollTo.getBoundingClientRect().left);
        const existingLeft = Math.abs(firstSlide.getBoundingClientRect().left - existingScrolledSlide.getBoundingClientRect().left);
        const lastSlide = this.getSlide(this.slideContainers?.length - 1);
        this.previousDisabled = false;
        this.nextDisabled = (lastSlide.getBoundingClientRect().right - (newLeft - existingLeft)) <= this.getContainer().getBoundingClientRect().right;
        this.slideIndex = newIndex;
        container.scrollTo({left: newLeft, behavior: 'smooth'});
    }
      
    private lastSlide(newIndex: number) {
        if (this.previousDisabled) return;

        if (newIndex < 0) {
            this.slideIndex = 0;
            return;
        };
        const container = this.getContainer();
        const firstSlide = this.getSlide(0);
        const slideToScrollTo = this.getSlide(newIndex);
        const newLeft = Math.abs(firstSlide.getBoundingClientRect().left - slideToScrollTo.getBoundingClientRect().left);
        this.previousDisabled = newIndex === 0;
        this.nextDisabled = false;
        this.slideIndex = newIndex;
        container.scrollTo({left: newLeft, behavior: 'smooth'});
    }
      
    private getSlide(index: number) {    
        return this.renderRoot?.querySelector(`#atl-wc-slideIndex${index}`) as HTMLElement;
    }  

    private getContainer() {    
        return this.renderRoot?.querySelector(`#atl-wc-carousel-container-id`) as HTMLElement;
    }  

    private setNavItemsVisibility(timer: number) {
        const firstElement = this.getSlide(0)?.getBoundingClientRect();        
        const lastElement = this.getSlide(this.slideContainers?.length === 0 ? 0 : this.slideContainers.length - 1)?.getBoundingClientRect();
        const container = this.getContainer()?.getBoundingClientRect();
        if (firstElement?.left === 0 || lastElement?.left === 0) {
            setTimeout(() => {
                this.setNavItemsVisibility(timer * 2);
            }, timer)
            return;
        }
        this.hideNavItems = !(firstElement?.left < container?.left || lastElement?.right >= container?.right)        
        this.setNavItemsBtnHeight(500, container?.height)
    }

    private setNavItemsBtnHeight(timer: number, height: number) {
        const container = this.renderRoot?.querySelector(`#atl-wc-carousel-root-container-id`) as HTMLElement;
        const buttons = this.renderRoot?.querySelector(`#atl-wc-carousel-btns-id`) as HTMLElement;

        if (!container) {
            setTimeout(() => {
                this.setNavItemsBtnHeight(timer * 2, height);
            }, timer)
            return;
        }
        container.style.height = `${height + 35}px`;
        if (buttons){
            buttons.style.display = `unset`;
        }
        this.previousDisabled = true;
        this.nextDisabled = false;
        this.render();
    }
}